import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Tables } from "@/integrations/supabase/types";

/**
 * Utility function to merge Tailwind CSS class names conditionally.
 * Used throughout the app for dynamic className construction.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Returns true if the given entry_date is more than 30 days ago.
 * Used to determine if a package is considered old for filtering or styling.
 */
export function isPackageOlderThan30Days(entry_date: string) {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return new Date(entry_date) < thirtyDaysAgo;
}

// Normalized data structure types
export interface NormalizedData {
  packages: Record<number, Tables<'packages'>>;
  users: Record<string, Tables<'profiles'>>;
  packageHistory: Record<number, Tables<'package_history'>[]>;
  lostItems: Record<number, Tables<'lost_items'>>;
}

// Helper functions for data normalization
export const normalizePackages = (packages: Tables<'packages'>[]) => {
  const normalized: Record<number, Tables<'packages'>> = {};
  packages.forEach(pkg => {
    normalized[pkg.id] = pkg;
  });
  return normalized;
};

export const normalizeUsers = (users: Tables<'profiles'>[]) => {
  const normalized: Record<string, Tables<'profiles'>> = {};
  users.forEach(user => {
    normalized[user.id] = user;
  });
  return normalized;
};

export const normalizeLostItems = (items: Tables<'lost_items'>[]) => {
  const normalized: Record<number, Tables<'lost_items'>> = {};
  items.forEach(item => {
    normalized[item.id] = item;
  });
  return normalized;
};

export const normalizePackageHistory = (history: Tables<'package_history'>[]) => {
  const normalized: Record<number, Tables<'package_history'>[]> = {};
  history.forEach(entry => {
    if (!normalized[entry.package_id]) {
      normalized[entry.package_id] = [];
    }
    normalized[entry.package_id].push(entry);
  });
  return normalized;
};

// Helper functions for accessing normalized data
export const getPackageWithUser = (
  packageId: number, 
  packages: Record<number, Tables<'packages'>>,
  users: Record<string, Tables<'profiles'>>
): (Tables<'packages'> & { user?: Tables<'profiles'> }) | null => {
  try {
    const pkg = packages[packageId];
    if (!pkg) return null;
    
    const user = users[pkg.last_updated_by];
    return {
      ...pkg,
      user
    };
  } catch (error) {
    console.error('Error getting normalized package:', error);
    return null;
  }
};

export const getLostItemWithUser = (
  itemId: number,
  items: Record<number, Tables<'lost_items'>>,
  users: Record<string, Tables<'profiles'>>
): (Tables<'lost_items'> & { user?: Tables<'profiles'> }) | null => {
  try {
    const item = items[itemId];
    if (!item) return null;
    
    const user = users[item.found_by || ''];
    return {
      ...item,
      user
    };
  } catch (error) {
    console.error('Error getting normalized lost item:', error);
    return null;
  }
};

export const getPackageHistory = (
  packageId: number,
  history: Record<number, Tables<'package_history'>[]>
): Tables<'package_history'>[] => {
  try {
    return history[packageId] || [];
  } catch (error) {
    console.error('Error getting package history:', error);
    return [];
  }
};

// Utility functions for data access
export const getPackagesArray = (packages: Record<number, Tables<'packages'>>): Tables<'packages'>[] => {
  return Object.values(packages || {});
};

export const getUsersArray = (users: Record<string, Tables<'profiles'>>): Tables<'profiles'>[] => {
  return Object.values(users || {});
};

export const getLostItemsArray = (items: Record<number, Tables<'lost_items'>>): Tables<'lost_items'>[] => {
  return Object.values(items || {});
};

// Type-safe selectors for common queries
export const selectPackageById = (
  packageId: number,
  packages: Record<number, Tables<'packages'>>
): Tables<'packages'> | null => {
  return packages[packageId] || null;
};

export const selectUserById = (
  userId: string,
  users: Record<string, Tables<'profiles'>>
): Tables<'profiles'> | null => {
  return users[userId] || null;
};

export const selectLostItemById = (
  itemId: number,
  items: Record<number, Tables<'lost_items'>>
): Tables<'lost_items'> | null => {
  return items[itemId] || null;
};
